import dayjs from 'dayjs';
import React from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_DEFAULTS } from './queryDefaults';
import { useWeekPrefs } from './usePersistedStates';
import { cleanTri } from '../helpers';
import baserowFetch from '../helpers/baserowFetch';

const { VITE_BASEROW_TOKEN: token,
  VITE_TABLE_ID_PRESENCES: presencesTableId } = import.meta.env;

const headers = {
  Authorization: `Token ${token}`,
  'Content-Type': 'application/json',
};

/**
 * Tell whether a presence is the optimistic copy of a row whose creation is
 * still pending: its id is then the temporary `key` string, unknown to
 * Baserow, while real row ids are numbers.
 *
 * @param {Object} [presence] - Presence object.
 * @returns {boolean} True while the presence has no Baserow id yet.
 */
const isPendingCreation = presence => typeof presence?.id === 'string';

/**
 * React hook to manage presences for a given place.
 * Provides CRUD operations and fetches presences for a time span based on user preferences.
 *
 * @function
 * @param {string} place - The name of the place to fetch and manage presences.
 * @returns {Object} An object containing:
 *   - presences: Array of presence objects.
 *   - setPresence: Function to create/update/delete a presence.
 *   - createPresence: Function to create a presence.
 *   - deletePresence: Function to delete a presence.
 *   - createRow, updateRow, deleteRow: Internal mutation hooks.
 * @example
 * const { presences, setPresence } = usePresences('Toulouse');
 * setPresence({ day: '2024-06-01', tri: 'ABC', plan: 'Toulouse', spot: 'A1' });
 */
const usePresences = place => {
  const [weekPref] = useWeekPrefs();

  let timespan = 14;
  if ([1, 2, 3].includes(parseInt(weekPref, 10))) timespan = parseInt(weekPref, 10) * 7;

  const today = dayjs(dayjs().format('YYYY-MM-DD')); // Wacky trick to strip time
  const dayFrom = today.day(0).format('YYYY-MM-DD');
  const dayTo = today.day(timespan).format('YYYY-MM-DD');

  const queryClient = useQueryClient();
  const basePath = `https://api.baserow.io/api/database/rows/table/${presencesTableId}/`;

  const queryKey = ['presences', place, dayFrom, dayTo];

  const qs = [
    '?',
    'user_field_names=true',
    `filter__day__date_after=${dayFrom}`,
    `filter__day__date_before=${dayTo}`,
    `filter__plan__equal=${place}`,
    'size=200',
  ].join('&');

  const { data: { results: presences = [] } = {} } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await baserowFetch(
        basePath + qs,
        { headers },
      );

      const nextData = await response.json();
      const prevData = queryClient.getQueryData(queryKey);

      if (prevData && Array.isArray(nextData.results)) {
        // Transpose `prevData.results` as object for quicker finding existing items
        const prevResults = prevData.results?.reduce(
          (acc, curr) => ({ ...acc, [curr.id]: curr }),
          {},
        );

        // Replace unchanged new items with pre-existing items
        nextData.results = nextData.results.map(nextResult => {
          const prevResult = prevResults[nextResult.id] || {};
          const prevHash = JSON.stringify([prevResult.fake, prevResult.spot]);
          const newHash = JSON.stringify([nextResult.fake, nextResult.spot]);

          return prevHash === newHash ? prevResult : nextResult;
        });
      }

      return nextData;
    },
    ...QUERY_DEFAULTS,
  });

  const createRow = useMutation({
    mutationFn: async record => {
      const response = await baserowFetch(
        `${basePath}?user_field_names=true`,
        { headers, method: 'POST', body: JSON.stringify(record) },
      );
      return response.json();
    },
    onMutate: async record => {
      queryClient.setQueryData(queryKey, previous => ({
        results: [
          ...previous.results,
          { ...record, fake: true, id: record.key },
        ],
      }));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const updateRow = useMutation({
    mutationFn: async record => {
      const response = await baserowFetch(
        `${basePath}${record.id}/?user_field_names=true`,
        { headers, method: 'PATCH', body: JSON.stringify(record) },
      );
      return response.json();
    },
    onMutate: async record => {
      queryClient.setQueryData(queryKey, ({ results = [] }) => ({
        results: results.map(result => (
          result.id === record.id
            ? { ...result, ...record, fake: true }
            : result
        )),
      }));
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const deleteRow = useMutation({
    mutationFn: record => baserowFetch(
      `${basePath}${record.id}/`,
      { headers, method: 'DELETE' },
    ),
    onMutate: async record => {
      queryClient.setQueryData(queryKey, ({ results = [] }) => ({
        results: results.filter(result => (result.id !== record.id)),
      }));
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const createPresence = React.useCallback(
    (date, tri, changes) => {
      const isoDate = date?.format?.('YYYY-MM-DD') || date;

      return createRow.mutate({
        key: `${isoDate}-${cleanTri(tri)}`,
        day: isoDate,
        tri: cleanTri(tri),
        ...changes,
      });
    },
    [createRow],
  );

  // Changes to a presence still being created are dropped: Baserow would
  // answer 404 on its temporary id.
  const deletePresence = React.useCallback(
    presence => (isPendingCreation(presence) ? null : deleteRow.mutate(presence)),
    [deleteRow],
  );

  const setPresence = React.useCallback(
    presence => {
      const { id, day, tri, plan, spot, period } = presence;
      if (isPendingCreation(presence)) {
        return null;
      }

      if (id && !spot) {
        return deleteRow.mutate(presence);
      }

      if (id && spot) {
        return updateRow.mutate(presence);
      }

      if (!id && (day && tri && plan && spot)) {
        createPresence(day, tri, { spot, plan, period });
      }

      return null;
    },
    [createPresence, deleteRow, updateRow],
  );

  return {
    presences,
    setPresence,
    createPresence,
    deletePresence,
    createRow,
    updateRow,
    deleteRow,
  };
};

export default usePresences;
